import lodashPick from 'lodash/fp/pick';

import { makeListingsFilters } from 'common/listingHelpers';
import safeLocalStorage from 'client/lib/safeLocalStorage';
import { getQualifiedHomesSearchFilters } from 'common/householdApplication/prequalificationHelpers';
import {
  SR_API_ACTION_GET_LISTING_LIST,
  SR_API_ACTION_GET_LISTINGS_QUALIFIED_TO_RENT,
  SR_API_ACTION_GET_MOST_POPULAR_LISTINGS,
  SR_API_ACTION_GET_FURTHER_COMMUTE_LISTINGS,
  SR_API_ACTION_GET_SPECIFIC_APARTMENTS_LISTINGS,
  SR_API_ACTION_GET_LAST_VISITED_LISTINGS,
  SR_API_ACTION_APARTMENT_GET_LIST,
} from 'api/actionTypes';
import { LISTING_DATA_SOURCE_SUNROOM } from 'common/listingConstants';

export const FEED_CATEGORY_QUALIFIED_TO_RENT = 'qualified-to-rent';
export const FEED_CATEGORY_BEST_MATCHES = 'best-matches';
export const FEED_CATEGORY_MOST_POPULAR = 'most-popular';
export const FEED_CATEGORY_FURTHER_COMMUTE = 'further-commute';
export const FEED_CATEGORY_STAY_IN_YOUR_BUDGET = 'stay-in-your-budget';
export const FEED_CATEGORY_STAY_IN_YOUR_LOCATION = 'stay-in-your-location';
export const FEED_CATEGORY_AREA_TO_TOUR = 'area-to-tour';
export const FEED_CATEGORY_SELF_TOUR = 'self-tour';
export const FEED_CATEGORY_EXCLUSIVE_APARTMENTS = 'exclusive-aparments';
export const FEED_CATEGORY_SPECIFIC_APARTMENTS = 'specific-aparments';
export const FEED_CATEGORY_LAST_VISITED = 'last-visited';
export const FEED_CATEGORY_LAST_VISITED_ANONYMOUS = 'last-visited-anonymous';
export const FEED_CATEGORY_SUNROOM_LISTINGS = 'sunroom-listings';

export const FEED_CATEGORY_LIST = [
  FEED_CATEGORY_QUALIFIED_TO_RENT,
  FEED_CATEGORY_BEST_MATCHES,
  FEED_CATEGORY_SELF_TOUR,
  FEED_CATEGORY_MOST_POPULAR,
  FEED_CATEGORY_FURTHER_COMMUTE,
  FEED_CATEGORY_STAY_IN_YOUR_BUDGET,
  FEED_CATEGORY_STAY_IN_YOUR_LOCATION,
  FEED_CATEGORY_AREA_TO_TOUR,
  FEED_CATEGORY_EXCLUSIVE_APARTMENTS,
  FEED_CATEGORY_SPECIFIC_APARTMENTS,
  FEED_CATEGORY_LAST_VISITED,
  FEED_CATEGORY_LAST_VISITED_ANONYMOUS,
  FEED_CATEGORY_SUNROOM_LISTINGS,
];

export const FEED_CATEGORY_BY_ID = {
  [FEED_CATEGORY_QUALIFIED_TO_RENT]: {
    id: FEED_CATEGORY_QUALIFIED_TO_RENT,
    title: 'Qualified to Rent ✅',
    subtitle: 'You’re approved to rent based on your income, move date, and pets.',
    noResultsSubtext:
      'Your qualifications are limiting your results. Please update them to see more rentals.', // eslint-disable-line max-len
    noResultsButtonText: 'Change your qualifications',
    action: SR_API_ACTION_GET_LISTINGS_QUALIFIED_TO_RENT,
    filters: ({ user, renterProfile, market }) => {
      return {
        ...makeListingsFilters({ user, market }),
        ...getQualifiedHomesSearchFilters({ renterProfile }),
      };
    },
  },
  [FEED_CATEGORY_BEST_MATCHES]: {
    id: FEED_CATEGORY_BEST_MATCHES,
    title: 'Best Matches 👌',
    subtitle: 'The perfect combo of price, location,\xa0\xa0and\xa0\xa0features.',
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ user, market }) => {
      return makeListingsFilters({ user, market });
    },
  },
  [FEED_CATEGORY_SELF_TOUR]: {
    id: FEED_CATEGORY_SELF_TOUR,
    // TODO(@Sunroom): Is this being used?
    // title: 'UPDATE ME',
    title: '',
    subtitle: 'Unlock these homes on your own, no agent or appointment required!',
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ user, market }) => {
      const listingsFilters = makeListingsFilters({ user, market });
      return {
        ...lodashPick(['marketId'], listingsFilters),
        selfTour: true,
      };
    },
  },
  [FEED_CATEGORY_MOST_POPULAR]: {
    id: FEED_CATEGORY_MOST_POPULAR,
    title: 'Most Popular ❤️',
    subtitle: 'The most saved and well rated based\xa0\xa0on\xa0\xa0people touring them.',
    action: SR_API_ACTION_GET_MOST_POPULAR_LISTINGS,
    filters: ({ user, market }) => {
      return makeListingsFilters({ user, market });
    },
  },
  [FEED_CATEGORY_FURTHER_COMMUTE]: {
    id: FEED_CATEGORY_FURTHER_COMMUTE,
    title: 'Further Commute 🚗',
    subtitle: 'Slightly outside your map search area.',
    action: SR_API_ACTION_GET_FURTHER_COMMUTE_LISTINGS,
    filters: ({ user, market }) => {
      return makeListingsFilters({ user, market });
    },
  },
  [FEED_CATEGORY_STAY_IN_YOUR_BUDGET]: {
    id: FEED_CATEGORY_STAY_IN_YOUR_BUDGET,
    title: 'Stay in your budget 💸',
    subtitle: 'But flex your location and features.',
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ user, market }) => {
      const listingsFilters = makeListingsFilters({ user, market });
      return lodashPick(['minRentPrice', 'maxRentPrice', 'pets', 'marketId'], listingsFilters);
    },
  },
  [FEED_CATEGORY_STAY_IN_YOUR_LOCATION]: {
    id: FEED_CATEGORY_STAY_IN_YOUR_LOCATION,
    title: 'Stay in your location 📍',
    subtitle: 'But flex your budget and features.',
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ user, market }) => {
      const listingsFilters = makeListingsFilters({ user, market });
      return lodashPick(['locationPoint', 'pets', 'marketId'], listingsFilters);
    },
  },
  [FEED_CATEGORY_AREA_TO_TOUR]: {
    id: FEED_CATEGORY_AREA_TO_TOUR,
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ user, market }) => {
      const listingsFilters = makeListingsFilters({ user, market });
      delete listingsFilters.locationPoint;
      return listingsFilters;
    },
  },
  [FEED_CATEGORY_EXCLUSIVE_APARTMENTS]: {
    title: 'Exclusive Apartments 🌟',
    subtitle: "Apartment communities you're gonna love.",
    action: SR_API_ACTION_APARTMENT_GET_LIST,
    filters: () => {
      return {
        withActiveUnits: true,
      };
    },
  },
  [FEED_CATEGORY_SPECIFIC_APARTMENTS]: {
    title: 'Preferred Property Managers',
    subtitle: 'Good leasing offices make great living experiences.',
    action: SR_API_ACTION_GET_SPECIFIC_APARTMENTS_LISTINGS,
  },
  [FEED_CATEGORY_LAST_VISITED]: {
    title: 'Recently Viewed 👀',
    subtitle: "Easily find rentals you've clicked on!",
    refreshOnUnmount: true,
    action: SR_API_ACTION_GET_LAST_VISITED_LISTINGS,
  },
  [FEED_CATEGORY_LAST_VISITED_ANONYMOUS]: {
    title: 'Recently Viewed 👀',
    subtitle: 'Easily find rentals that you were just looking at!',
    refreshOnUnmount: true,
    hideIfEmpty: true,
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: () => {
      const viewedListingIdsStr = safeLocalStorage.getItem('viewedListingIds');
      const viewedListingIds = viewedListingIdsStr ? JSON.parse(viewedListingIdsStr) : [];
      return {
        includeIds: viewedListingIds,
      };
    },
  },
  [FEED_CATEGORY_SUNROOM_LISTINGS]: {
    id: FEED_CATEGORY_SUNROOM_LISTINGS,
    title: 'Sunroom Listings',
    subtitle: 'Get a special touring and application experience with only these listings.',
    action: SR_API_ACTION_GET_LISTING_LIST,
    filters: ({ market }) => {
      return {
        dataSource: [LISTING_DATA_SOURCE_SUNROOM],
        excludeSmartApartmentDataSource: true,
        marketId: market.id,
      };
    },
  },
};
